import { registerSW } from 'virtual:pwa-register'

const PRELOAD_RELOAD_KEY = 'vite-preload-reload'
const SW_UPDATE_INTERVAL_MS = 15 * 60 * 1000

/** @type {EventTarget} */
export const pwaEvents = new EventTarget()

let needRefresh = false
/** @type {(() => Promise<void>) | undefined} */
let applyUpdate

export const getNeedRefresh = () => needRefresh

let swRegistration: ServiceWorkerRegistration | undefined

// workbox-window reloads only when the page was already controlled at load
// (isUpdate), so after a hard reload the prompt stayed forever. Reload on
// controllerchange ourselves; with no waiting worker there is nothing to
// activate, so just reload to drop the stale prompt.
export const applyPwaUpdate = () => {
  if (!swRegistration?.waiting) {
    window.location.reload()
    return
  }
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => window.location.reload(),
    { once: true },
  )
  return applyUpdate?.()
}

// After a deploy the old tab may import hashed chunks that no longer exist.
// Keep the flag for the tab lifetime (sessionStorage): clearing it on module
// load lets a still-missing chunk reload in a loop.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  if (sessionStorage.getItem(PRELOAD_RELOAD_KEY) === '1') return
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1')
  window.location.reload()
})

applyUpdate = registerSW({
  immediate: true,
  onNeedRefresh() {
    needRefresh = true
    pwaEvents.dispatchEvent(new Event('need-refresh'))
  },
  onRegisteredSW(swUrl, registration) {
    if (!registration) return
    swRegistration = registration

    const checkForUpdate = async () => {
      if (registration.installing || !navigator.onLine) return
      try {
        // swUrl is relative: on /a/b it would hit the SPA fallback, not the worker
        const resp = await fetch(registration.active?.scriptURL ?? swUrl, {
          cache: 'no-store',
          headers: { 'cache-control': 'no-cache' },
        })
        if (resp.ok) await registration.update()
      } catch {
        // offline or origin unreachable: keep the current worker
      }
    }

    setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS)
  },
})
